import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { inscreverNotificacoesPush } from '../utils/pushNotificacoes';
import { atualizarTokenDasFilas } from '../utils/rascunhoOffline';

// Resíduo da customização de cor, removida em 12/08/2026 (o sistema
// passou a ter uma cor só). A chave fica órfã no navegador de quem já
// usava; limpa aqui, uma vez, no carregamento — não dentro do logout,
// senão só sumiria de quem deslogasse.
/* Limpeza da cor de acento por usuário, aposentada em 12/08/2026.
     Silêncio de propósito: em aba anônima o localStorage lança, e não
     conseguir apagar uma chave que já não se usa não é problema de
     ninguém. */
  try { localStorage.removeItem('sgq_cor_acento'); } catch (e) { console.debug('[limpeza cor-acento]', e.message); }

const AuthContext = createContext(null);

// Aponta direto para o backend — funciona tanto em dev quanto em produção
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:4000/api'
  : '/api';

const API = axios.create({ baseURL: API_URL });

// Interceptor: adiciona token em todas as requisições
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('sgq_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* ══ RENOVAÇÃO DE SESSÃO (17/08/2026) ═══════════════════════════════
   O backend estende o token quando falta menos de uma hora e devolve o
   novo em `X-Token-Renovado`. Aqui ele é trocado em silêncio.

   Silêncio é o certo: a pessoa não precisa saber que a sessão foi
   renovada — ela precisa é não ser interrompida. Avisar transformaria
   um não-evento em interrupção.

   No interceptor de RESPOSTA e não em cada chamada: assim vale para
   todas, incluindo as que ainda serão escritas. */
/* UM interceptor de resposta, não dois (17/08/2026) — eu tinha somado
   um segundo só para a renovação, e dois fazendo coisas diferentes na
   mesma resposta é convite para alguém mexer num sem ver o outro.

   Sucesso: troca o token se o backend renovou.
   Erro 401: limpa a sessão e manda para o login. */
API.interceptors.response.use(
  (res) => {
    /* O backend estende o token quando falta menos de uma hora e devolve
       o novo neste cabeçalho. A troca é SILENCIOSA de propósito: a
       pessoa não precisa saber que a sessão foi renovada — precisa é não
       ser interrompida. Avisar transformaria um não-evento em
       interrupção. */
    const novoToken = res.headers?.['x-token-renovado'];
    if (novoToken) { localStorage.setItem('sgq_token', novoToken); atualizarTokenDasFilas(novoToken); }   // (o envio que não perde foto) a fila recebe o login renovado
    return res;
  },
  (err) => {
    /* ⚠⚠ O PRÓPRIO LOGIN FICA DE FORA, e este era um defeito VIVO
       (11/09/2026, achado ao capturar a tela de erro para a demo).

       O backend responde **401** para "Usuário ou senha incorretos" e
       para "Usuário desativado". Este interceptor tratava QUALQUER 401
       como sessão expirada e chamava `window.location.href = '/login'`
       — uma recarga inteira da página.

       ⚠ O RESULTADO NA TELA: quem errava a senha via a página recarregar
       e o formulário voltar em branco. **A mensagem de erro nunca
       aparecia**, porque o componente que ia mostrá-la era destruído
       junto com a página. E quem tinha o usuário desativado nunca
       descobria o motivo.

       A regra certa é: 401 na rota de LOGIN é resposta esperada — não há
       sessão para expirar, quem está ali ainda não entrou. 401 em
       qualquer outra rota é sessão morta e aí sim se limpa e se
       redireciona. */
    const rota = err.config?.url || '';
    const noLogin = rota.includes('/auth/login');
    if (err.response?.status === 401 && !noLogin) {
      localStorage.removeItem('sgq_token');
      localStorage.removeItem('sgq_usuario');
      localStorage.removeItem('sgq_acessos_cache');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  // Foto de perfil (31/07/2026, migrada de base64-no-localStorage pra
  // arquivo em disco) — usuario.foto agora é só um nome de arquivo,
  // não a imagem em si. fotoUrl guarda o Object URL já resolvido
  // (busca a imagem via API e converte pra blob), pros componentes
  // usarem direto num <img src>.
  const [fotoUrl, setFotoUrl] = useState(null);
  const carregarFotoUrl = async (usuarioId, nomeArquivoFoto) => {
    setFotoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (!usuarioId || !nomeArquivoFoto) return;
    try {
      const res = await API.get(`/auth/foto/${usuarioId}`, { responseType: 'blob' });
      setFotoUrl(URL.createObjectURL(res.data));
    } catch (e) {
      /* O fallback é intencional; o registro existe para a falha não
         virar mistério quando alguém for investigar. */
      console.error('[AuthContext/foto]', e.message);
    }
  };
  // Os acessos nascem do cache salvo no login (sgq_acessos_cache): a página
  // já monta com o menu pronto no primeiro paint, sem depender do timing da
  // chamada /acessos/meus — que continua rodando depois, só pra revalidar.
  // Motivo (15/07): mesmo com a chamada respondendo OK (confirmado por log),
  // a aplicação tardia dos acessos pós-mount não preenchia o menu no ambiente
  // real do Juan; com o cache, não existe mais "chegar tarde".
  const cacheAcessos = (() => {
    try { return JSON.parse(localStorage.getItem('sgq_acessos_cache') || 'null'); }
    catch { return null; }
  })();
  const [acessos, setAcessos] = useState(cacheAcessos?.acessos || {});
  const [isMaster, setIsMaster] = useState(!!cacheAcessos?.master);
  const [admDe, setAdmDe] = useState(cacheAcessos?.adm_de || []);
  // SGQ RH (07/08/2026) — leve de propósito: só "tenho acesso ou não",
  // pra decidir se o seletor de portal aparece depois do login. O mapa
  // de acessos DENTRO do RH (quais salas, qual nível) só é buscado
  // quando a pessoa efetivamente entra no portal RH — não faz sentido
  // buscar isso pra todo mundo que loga, se a esmagadora maioria nunca
  // vai ter acesso lá.
  const [podeAcessarRH, setPodeAcessarRH] = useState(false);
  // Administração Geral (09/08/2026) — mesmo padrão leve do RH acima.
  const [podeAcessarGeral, setPodeAcessarGeral] = useState(false);
  // SGQ (09/08/2026, achado real — pergunta do Juan): faltava o
  // equivalente pro próprio SGQ — sem isso, o Seletor de Portal
  // sempre contava "SGQ" como opção garantida, mesmo pra quem (Master
  // RH/Geral) não tem nenhum vínculo real lá. Mesmo padrão leve das
  // outras duas.
  const [podeAcessarSGQ, setPodeAcessarSGQ] = useState(false);
  // Achado real ao testar num navegador de verdade (09/08/2026): como
  // as duas chamadas acima são assíncronas e não bloqueiam nada, o
  // RotaPublica (decide /portal vs /dashboard depois do login) corria
  // ANTES delas responderem — sempre via podeAcessarRH/Geral ainda
  // como 'false' (o valor inicial), mandava todo mundo pro SGQ, e
  // como a correção nunca dispara de novo depois (o componente já
  // saiu da tela), a pessoa ficava PRESA no SGQ mesmo tendo acesso ao
  // RH/Geral — só descobriria digitando /portal na mão. Essa flag
  // deixa só a decisão do Seletor esperar essas duas (rápidas, 5s de
  // timeout no pior caso) — o resto do app (Dashboard, salas) continua
  // sem esperar nada, do jeito que já era de propósito.
  const [acessosExtrasProntos, setAcessosExtrasProntos] = useState(false);
  const [carregando, setCarregando] = useState(true);
  // Marca que os acessos estão disponíveis pro usuário atual. Se o cache do
  // login existe, já nasce true — a tela não fica presa esperando e o menu
  // vem pronto no primeiro paint. Sem cache (primeiro login com falha, cache
  // limpo), as rotas protegidas seguram até o /acessos/meus responder.
  const [acessosProntos, setAcessosProntos] = useState(!!cacheAcessos);

  const [tema, setTemaState] = useState(() => {
    const salvo = localStorage.getItem('sgq_tema');
    // Escuro é o padrão do sistema (claro é opção do usuário) — quem
    // nunca abriu o sistema neste navegador (sem nada salvo ainda,
    // ex: tela de login na primeira visita) nasce em 'dark'.
    return salvo || 'dark';
  });

  const [navEstilo, setNavEstiloState] = useState(() => {
    const salvo = localStorage.getItem('sgq_nav_estilo');
    return salvo || 'sidebar';
  });

  // Preferência de exibição da SidebarNova (compacta/estendida) — mesmo
  // padrão do navEstilo. localStorage guarda 'true'/'false' como texto.
  const [sidebarCompacta, setSidebarCompactaState] = useState(() => {
    return localStorage.getItem('sgq_sidebar_compacta') === 'true';
  });

  /* ── TAMANHO DA LEITURA (25/08/2026) ──────────────────────────
     Preferência da pessoa, como o tema. Quem precisa de letra maior
     precisa em TODA tela — um alternador por tela obrigaria a ligar de
     novo a cada conferência, e com a peça na mão isso não acontece.

     `localStorage` primeiro para não PISCAR: sem ele a tela abriria no
     tamanho normal e cresceria quando o login respondesse. O servidor
     é a fonte da verdade e sobrescreve quando chega. */
  const [escalaLeitura, setEscalaLeituraState] = useState(
    () => localStorage.getItem('sgq_escala_leitura') || 'normal');

  useEffect(() => {
    document.documentElement.setAttribute('data-tema', tema);
  }, [tema]);

  useEffect(() => {
    document.documentElement.setAttribute('data-escala', escalaLeitura);
    localStorage.setItem('sgq_escala_leitura', escalaLeitura);
  }, [escalaLeitura]);

  const setEscalaLeitura = useCallback(async (valor) => {
    if (!['normal', 'medio', 'ampliado'].includes(valor)) return;
    /* Aplica na hora e salva depois: a pessoa está escolhendo o tamanho
       olhando a tela, e esperar a rede para ver o efeito torna a escolha
       impossível de avaliar. Se a gravação falhar, o localStorage segura
       até a próxima entrada. */
    setEscalaLeituraState(valor);
    try {
      await API.put('/auth/escala-leitura', { escala_leitura: valor });
    } catch (e) {
      console.error('[escalaLeitura]', e.message);
    }
  }, []);

  const carregarAcessos = useCallback(async () => {
    // Rede de segurança: aconteça o que acontecer, a tela NUNCA fica presa
    // no "Carregando..." — libera em no máximo 5s. Se os acessos chegarem
    // depois disso (retry), o menu preenche sozinho, pois setAcessos
    // re-renderiza tudo que consome o contexto.
    let liberou = false;
    const liberar = () => { if (!liberou) { liberou = true; setAcessosProntos(true); } };
    const failsafe = setTimeout(liberar, 5000);

    // Até 3 tentativas com espera curta entre elas. Cobre o hiccup real
    // observado em produção (15/07): logo após o login, /acessos/meus às
    // vezes falha no navegador (config e outras chamadas passam), o menu
    // de salas vinha vazio e só o F5 resolvia. IMPORTANTE: o erro agora é
    // LOGADO — a versão anterior engolia o erro em silêncio, o que deixava
    // o console limpo e impedia o diagnóstico.
    const esperas = [0, 800, 2000];
    for (let tent = 0; tent < esperas.length; tent++) {
      if (esperas[tent]) await new Promise(r => setTimeout(r, esperas[tent]));
      try {
        // timeout de 5s POR TENTATIVA: sem isso, uma requisição pendurada
        // (conexão zumbi — visto em produção 15/07: console sem NENHUM log,
        // nem ok nem falha, porque a promise nunca resolvia) travava o loop
        // pra sempre e o retry nunca disparava. Com timeout, ela rejeita,
        // loga, e a tentativa seguinte abre conexão nova — que funciona.
        const res = await API.get('/acessos/meus', { timeout: 5000 });
        const { acessos: mapa, master, adm_de } = res.data;
        setAcessos(mapa || {});
        setIsMaster(!!master);
        setAdmDe(adm_de || []);
        // mantém o cache fresco pro próximo load nascer com o menu certo
        /* Cache de acessos: acelera o primeiro render depois do login. Falhar
       aqui (cota cheia, aba anônima) só torna a próxima abertura mais
       lenta — o sistema busca do servidor de qualquer jeito. */
      try { localStorage.setItem('sgq_acessos_cache', JSON.stringify(res.data)); } catch (e) { console.debug('[cache de acessos]', e.message); }
        console.log(`[SGQ acessos] ok na tentativa ${tent + 1} — ${Object.keys(mapa || {}).length} salas, master=${!!master}`);
        break;
      } catch (e) {
        console.error(`[SGQ acessos] falha na tentativa ${tent + 1}/${esperas.length}:`, e?.response?.status || e?.message || e);
      }
    }
    clearTimeout(failsafe);
    liberar();

    // SGQ RH — chamada separada, não bloqueia o failsafe acima (o SGQ
    // não pode ficar esperando o RH responder pra liberar a tela).
    Promise.allSettled([
      API.get('/acessos/tenho-acesso', { timeout: 5000 })
        .then(res => setPodeAcessarSGQ(!!res.data?.tem_acesso))
        .catch(() => setPodeAcessarSGQ(false)),
      API.get('/rh/acessos/tenho-acesso', { timeout: 5000 })
        .then(res => setPodeAcessarRH(!!res.data?.tem_acesso))
        .catch(() => setPodeAcessarRH(false)),
      API.get('/administracao-geral/tenho-acesso', { timeout: 5000 })
        .then(res => setPodeAcessarGeral(!!res.data?.tem_acesso))
        .catch(() => setPodeAcessarGeral(false)),
    ]).then(() => setAcessosExtrasProntos(true));
  }, []);

  // Recupera sessão salva no localStorage
  useEffect(() => {
    const token = localStorage.getItem('sgq_token');
    const usuarioSalvo = localStorage.getItem('sgq_usuario');
    if (token && usuarioSalvo) {
      const user = JSON.parse(usuarioSalvo);
      setUsuario(user);
      carregarFotoUrl(user.id, user.foto);
      carregarAcessos();
      /* ⚠ TAMBÉM AO RESTAURAR A SESSÃO (28/08/2026, relato do Juan: "as
         notificações de push ainda não estão vindo").

         A inscrição só acontecia dentro do `login`. No celular ninguém
         desloga — a sessão fica no `localStorage` e a pessoa entra
         direto — então quem já estava logado quando o push foi criado
         NUNCA foi inscrito, e nunca seria.

         Aqui ela roda a cada abertura do app. É barato: a função sai
         cedo se o navegador não suporta ou se a permissão foi negada, e
         o `POST` grava por endpoint, então repetir não duplica. */
      inscreverNotificacoesPush(API);
    } else {
      setAcessosProntos(true); // sem sessão → nada a carregar, libera as rotas públicas
    }
    setCarregando(false);
  }, [carregarAcessos]);

  const login = async (loginUsuario, senha, manterConectado) => {
    const res = await API.post('/auth/login', { usuario: loginUsuario, senha, manterConectado });
    const { token, usuario: user } = res.data;
    localStorage.setItem('sgq_token', token);
    atualizarTokenDasFilas(token);   // (o envio que não perde foto) o que esperava esta pessoa entrar de novo segue para o servidor
    localStorage.setItem('sgq_usuario', JSON.stringify(user));
    localStorage.setItem('sgq_tema', user.tema || 'dark');
    localStorage.setItem('sgq_nav_estilo', user.navEstilo || 'sidebar');
    localStorage.setItem('sgq_sidebar_compacta', String(!!user.sidebarCompacta));
    // ATENÇÃO (15/07/2026): aqui existia uma chamada a setEmpresaSessao — um
    // resquício do tempo em que havia sessão de empresa. O estado foi removido
    // num refactor, mas a chamada ficou: um ReferenceError explodia AQUI em
    // TODO login, era engolido pelo catch do formulário, e nada depois desta
    // linha rodava (nem o redirect correto). Era ESTA a raiz de todos os
    // episódios do "menu vazio até dar F5". Não reintroduzir.
    setUsuario(user);
    carregarFotoUrl(user.id, user.foto);
    setTemaState(user.tema || 'dark');
    setNavEstiloState(user.navEstilo || 'sidebar');
    /* O servidor manda: se a pessoa entrou noutro aparelho e mudou lá, é
       esse o valor que vale — o localStorage só evita o piscar. */
    setEscalaLeituraState(user.escalaLeitura || 'normal');
    setSidebarCompactaState(!!user.sidebarCompacta);
    // Busca os acessos AGORA e guarda em cache local — a página que nasce
    // após o reload (Login.jsx faz window.location.assign) já monta com o
    // menu pronto, sem depender de nenhuma chamada dar certo na hora certa.
    // Se falhar aqui, segue sem cache: o fluxo pós-reload (retry + timeout
    // + failsafe) assume como rede de segurança.
    try {
      const ac = await API.get('/acessos/meus', { timeout: 5000 });
      localStorage.setItem('sgq_acessos_cache', JSON.stringify(ac.data));
    } catch (e) {
      console.error('[SGQ acessos] pré-carga no login falhou (segue sem cache):', e?.response?.status || e?.message || e);
    }
    // Pede permissão de notificação e inscreve o dispositivo — nunca
    // bloqueia o login (best-effort, roda em paralelo com o redirect).
    inscreverNotificacoesPush(API);
    return user;
  };

  const logout = useCallback(async () => {
    /* O logout local acontece SEMPRE, mesmo se o servidor não responder:
     a sessão do navegador é limpa logo abaixo. Avisar de falha aqui
     assustaria sem motivo — a pessoa quis sair, e ela saiu. */
    try { await API.post('/auth/logout'); } catch (e) { console.debug('[logout no servidor]', e.message); }
    localStorage.removeItem('sgq_token');
    localStorage.removeItem('sgq_usuario');
    localStorage.removeItem('sgq_acessos_cache');
    setUsuario(null);
    setAcessos({});
    setIsMaster(false);
    setAcessosProntos(true); // volta pra tela de login sem travar
  }, []);

  const trocarSenha = async (senhaAtual, novaSenha) => {
    await API.put('/auth/senha', { senhaAtual, novaSenha });
    const userAtualizado = { ...usuario, primeiroAcesso: false };
    localStorage.setItem('sgq_usuario', JSON.stringify(userAtualizado));
    setUsuario(userAtualizado);
  };

  // Altera tema — só salva localmente na tela de login (sem usuário logado)
  const alterarTema = async (novoTema) => {
    setTemaState(novoTema);
    localStorage.setItem('sgq_tema', novoTema);
    if (usuario) {
      const userAtualizado = { ...usuario, tema: novoTema };
      localStorage.setItem('sgq_usuario', JSON.stringify(userAtualizado));
      setUsuario(userAtualizado);
      try { await API.put('/auth/tema', { tema: novoTema }); } catch (e) { console.debug('[preferencia de interface]', e.message); }
    }
  };

  // Altera estilo de navegação (sidebar/dock) — mesmo padrão do tema:
  // atualiza local primeiro (feedback imediato), depois persiste no servidor.
  const alterarNavEstilo = async (novoEstilo) => {
    setNavEstiloState(novoEstilo);
    localStorage.setItem('sgq_nav_estilo', novoEstilo);
    if (usuario) {
      const userAtualizado = { ...usuario, navEstilo: novoEstilo };
      localStorage.setItem('sgq_usuario', JSON.stringify(userAtualizado));
      setUsuario(userAtualizado);
      try { await API.put('/auth/nav-estilo', { nav_estilo: novoEstilo }); } catch (e) { console.debug('[preferencia de interface]', e.message); }
    }
  };

  // Sidebar compacta ou estendida — mesmo padrão do tema/nav_estilo.
  // Antes era só useState local dentro da SidebarNova (se perdia ao
  // recarregar); fechado a pedido do Juan pra persistir de verdade.
  const alterarSidebarCompacta = async (novoValor) => {
    setSidebarCompactaState(novoValor);
    localStorage.setItem('sgq_sidebar_compacta', String(novoValor));
    if (usuario) {
      const userAtualizado = { ...usuario, sidebarCompacta: novoValor };
      localStorage.setItem('sgq_usuario', JSON.stringify(userAtualizado));
      setUsuario(userAtualizado);
      try { await API.put('/auth/sidebar-compacta', { sidebar_compacta: novoValor }); } catch (e) { console.debug('[preferencia de interface]', e.message); }
    }
  };

  // Foto de perfil (06/07/2026) — mesmo padrão do tema: atualiza
  // local primeiro (feedback imediato), depois persiste no servidor.
  const alterarFoto = async (novaFoto) => {
    const res = await API.put('/auth/foto', { foto: novaFoto });
    const userAtualizado = { ...usuario, foto: res.data.foto };
    localStorage.setItem('sgq_usuario', JSON.stringify(userAtualizado));
    setUsuario(userAtualizado);
    await carregarFotoUrl(userAtualizado.id, res.data.foto);
    return res.data;
  };

  /* ⚠ AQUI VIVIA O `alterarLogin` (06/09 → 08/09/2026). Saiu com a rota
     `PUT /auth/login`: trocar login é ato de administração, e o painel
     administrativo já o faz. Ver a nota no `authController`. */

  // ── Vocabulário do modelo de permissões (§1 da especificação) ──────────
  const RANK = { nenhum: 0, ver: 1, editar: 2, gerenciar: 3 };
  const pode = (sala, areaOuNivel, nivelMinimo) => {
    if (isMaster) return true;
    let chave = String(sala);
    let minimo = 'ver';
    if (nivelMinimo) { chave = `${sala}›${areaOuNivel}`; minimo = nivelMinimo; }
    else if (areaOuNivel) { minimo = areaOuNivel; }
    const efetivo = acessos[chave]?.nivel || 'nenhum';
    return RANK[efetivo] >= RANK[minimo];
  };
  const escopoDe = (sala, area) => {
    const chave = area ? `${sala}›${area}` : String(sala);
    return acessos[chave] || { nivel: 'nenhum' };
  };

  return (
    <AuthContext.Provider value={{
      usuario, fotoUrl, carregando, tema, navEstilo, sidebarCompacta,
      escalaLeitura, setEscalaLeitura,
      login, logout, trocarSenha, alterarTema, alterarNavEstilo, alterarSidebarCompacta, alterarFoto, API,
      acessos, isMaster, admDe, pode, escopoDe, recarregarAcessos: carregarAcessos, acessosProntos,
      podeAcessarSGQ, podeAcessarRH, podeAcessarGeral, acessosExtrasProntos }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
};

export { API };
